import {
  ReactNode, useMemo, useCallback, useRef, useEffect, useState,
} from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router";
import { JOBS_PAGE_PATH } from "@/shared/configs/routes.config";
import type { OpenJobModalOptions } from "@/shared/contexts/JobContext";
import { reconnectPusher } from "@/shared/services/pusher.service";
import { RootState } from "@/store";
import {
  BASE_JOBS_CHANNEL_NAME,
  PUSHER_RECONNECT_DELAY,
  JOB_REFRESH_DELAY,
  NOTIFICATION_DELAY,
} from "../configs/constants";
import { useInProgressJobsSeed } from "../hooks/useInProgressJobsSeed";
import { useJobModal } from "../hooks/useJobModal";
import { jobNotificationSystem } from "../hooks/useJobNotifications";
import { useJobUpdates } from "../hooks/useJobUpdates";
import { jobDataManager } from "../services/jobDataManager";
import { getJob } from "../services/jobs.service";
import { isComplete, isGaugeMoving } from "../utils/job-status.utils";
import { JobContext } from "./JobContext";
import type { Job, PushedJob } from "../types/jobs.types";

const getOrganizationId = (state: RootState): string => state.session.user?.representative?.organization_id ?? "";
const getChannelName = (organizationId: string): string => `${BASE_JOBS_CHANNEL_NAME}${organizationId}`;
const isJobsPage = (pathname: string): boolean => pathname.includes(JOBS_PAGE_PATH);
const fetchJobByUuid = async (uuid: string): Promise<Job | null> => {
  try {
    const { data } = await getJob(uuid);
    return data.job;
  }
  catch {
    return null;
  }
};
const runJobCompletionCallback = (jobUuid: string, callbacks: Map<string, () => void>): void => {
  const callback = callbacks.get(jobUuid);
  if (callback) {
    callback();
    callbacks.delete(jobUuid);
  }
};

const shouldHandleJob = (job: PushedJob, isJobsPageVisible: boolean, isJobModalOpen: boolean): boolean =>
  isComplete(job) && (isJobsPageVisible || isJobModalOpen);

const refreshInProgressJobs = async (
  inProgressJobs: PushedJob[],
  addJobToPushedJobs: (job: PushedJob) => void,
): Promise<void> => {
  const refreshPromises = inProgressJobs.map(async (job) => {
    try {
      const refreshedJob = await fetchJobByUuid(job.uuid);
      if (refreshedJob && (refreshedJob.status_code !== job.status_code || refreshedJob.percent !== job.percent)) {
        const updatedPushedJob = {
          ...job,
          ...refreshedJob,
          timestamp: Date.now(),
        };
        addJobToPushedJobs(updatedPushedJob);
      }
    }
    catch {
      // Job refresh failed - silently continue with other jobs
    }
  });

  try {
    await Promise.all(refreshPromises);
  }
  catch {
    // Some job refreshes failed - this is non-critical, continue silently
  }
};

export const JobProvider = ({ children }: { children: ReactNode }) => {
  const organizationId = useSelector(getOrganizationId);
  const channelName = useMemo(() => getChannelName(organizationId), [organizationId]);

  const location = useLocation();
  const isJobsPageVisible = isJobsPage(location.pathname);

  const {
    currentJob, isJobModalOpen, openJobModal: baseOpenJobModal, closeJobModal: baseCloseJobModal,
  } = useJobModal();

  const jobCompletionCallbacksRef = useRef<Map<string, () => void>>(new Map());

  // Arms post-success auto-navigation only for a job opened right after its action;
  // cleared on close so reopening the same job never auto-navigates again.
  const [autoNavigateJobUuid, setAutoNavigateJobUuid] = useState<string | null>(null);

  const handleJobCompleted = useCallback(
    async (job: PushedJob): Promise<void> => {
      // Only handle when job is actually completed, not on every progress update
      if (!isComplete(job)) return;

      if (!shouldHandleJob(job, isJobsPageVisible, isJobModalOpen)) return;

      runJobCompletionCallback(job.uuid, jobCompletionCallbacksRef.current);
    },
    [isJobsPageVisible, isJobModalOpen],
  );

  const {
    pushedJobs, lastPushedJob, addJobToPushedJobs, seedInProgressJobs, resetJobs,
  } = useJobUpdates(channelName, handleJobCompleted);

  useInProgressJobsSeed(seedInProgressJobs, resetJobs, organizationId);

  // Update external job data manager when pushedJobs changes
  useEffect(() => {
    jobDataManager.updateJobs(pushedJobs, lastPushedJob);
  }, [pushedJobs, lastPushedJob]);

  // Refs let the visibility-change effect read the latest pushedJobs /
  // addJobToPushedJobs without subscribing to them in its deps. Without these,
  // the effect re-attaches on every Pusher message (pushedJobs is a new Map
  // each time), and each attach left dangling setTimeout calls behind.
  const pushedJobsRef = useRef(pushedJobs);
  useEffect(() => {
    pushedJobsRef.current = pushedJobs;
  }, [pushedJobs]);

  const addJobToPushedJobsRef = useRef(addJobToPushedJobs);
  useEffect(() => {
    addJobToPushedJobsRef.current = addJobToPushedJobs;
  }, [addJobToPushedJobs]);

  useEffect(() => {
    const timeouts: ReturnType<typeof setTimeout>[] = [];
    let cancelled = false;

    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;

      timeouts.push(setTimeout(() => {
        if (cancelled) return;
        reconnectPusher();
      }, PUSHER_RECONNECT_DELAY));

      if (pushedJobsRef.current.size > 0) {
        timeouts.push(setTimeout(async () => {
          // clearTimeout cannot stop an already-started async callback, so we
          // re-check `cancelled` before and after every async hop to avoid
          // calling addJobToPushedJobs on an unmounted provider.
          if (cancelled) return;
          const inProgressJobs = Array.from(pushedJobsRef.current.values()).filter(isGaugeMoving);
          await refreshInProgressJobs(inProgressJobs, (job) => {
            if (cancelled) return;
            addJobToPushedJobsRef.current(job);
          });
        }, JOB_REFRESH_DELAY));
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      cancelled = true;
      timeouts.forEach(clearTimeout);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const openJobModal = useCallback(async (job: Job, options?: OpenJobModalOptions): Promise<void> => {
    if (!job.uuid) return;

    setAutoNavigateJobUuid(options?.autoNavigate ? job.uuid : null);

    const existingPushedJob = jobDataManager.getPushedJob(job.uuid);
    if (existingPushedJob && isComplete(existingPushedJob)) {
      const refreshedJob = {
        ...job,
        ...existingPushedJob,
      };
      baseOpenJobModal(refreshedJob);
      return;
    }

    baseOpenJobModal(job);
  }, [baseOpenJobModal]);

  const onJobComplete = useCallback((job: Job, callback: () => void) => {
    if (isComplete(job)) {
      callback();
      return;
    }
    jobCompletionCallbacksRef.current.set(job.uuid, callback);
  }, []);

  // Function to immediately add a newly created job to pushedJobs for instant badge update
  const addNewJobToPushedJobs = useCallback((job: Job) => {
    addJobToPushedJobs(job);
  }, [addJobToPushedJobs]);

  const closeJobModal = useCallback(() => {
    setAutoNavigateJobUuid(null);
    baseCloseJobModal();

    if (currentJob) {
      setTimeout(() => {
        jobNotificationSystem.notify(currentJob.uuid);
      }, NOTIFICATION_DELAY);
    }
  }, [baseCloseJobModal, currentJob]);

  const contextValue = useMemo(
    () => ({
      isJobModalOpen,
      currentJob,
      autoNavigateJobUuid,
      openJobModal,
      closeJobModal,
      onJobComplete,
      addNewJobToPushedJobs,
    }),
    [
      isJobModalOpen,
      currentJob?.uuid,
      autoNavigateJobUuid,
      openJobModal,
      closeJobModal,
      onJobComplete,
      addNewJobToPushedJobs,
    ],
  );

  return (
    <JobContext.Provider value={contextValue}>
      {children}
    </JobContext.Provider>
  );
};
